import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, Menu, Search, X } from 'lucide-react';
import { hasAnyRole, ROLE_LABELS, type AuthUser } from '@mirsad/shared';
import { useCurrentUser, useLogout } from '@/features/auth/useAuth';
import { useCompteurAvis } from '@/features/avis/useAvis';
import { cx } from '@/lib/cx';
import { definirDevises, useDevises } from '@/lib/devise';
import { LogoMarque } from './LogoMarque';
import { NAV } from './nav';

/**
 * Avis à 1 ou 2 étoiles des 7 derniers jours, dans le périmètre du compte. Une
 * mesure, pas une file de tâches : il n'y a rien à marquer comme lu.
 */
function CompteurAvisFaibles() {
  const { data } = useCompteurAvis();
  if (!data?.faibles) return null;
  const texte = `${data.faibles.toLocaleString('fr-FR')} avis à 1 ou 2 étoiles ces ${data.jours} derniers jours`;
  return (
    <>
      <span
        aria-hidden="true"
        title={texte}
        className="ml-auto min-w-[1.25rem] rounded-full bg-error px-1.5 text-center text-caption font-semibold leading-5 text-white tabular-nums"
      >
        {data.faibles > 99 ? '99+' : data.faibles}
      </span>
      <span className="sr-only">, {texte}</span>
    </>
  );
}

function Sidebar({
  utilisateur,
  ouvert,
  onFermer,
}: {
  utilisateur: AuthUser;
  ouvert: boolean;
  onFermer: () => void;
}) {
  const sections = NAV.map((s) => ({
    ...s,
    entrees: s.entrees.filter((e) => !e.roles || hasAnyRole(utilisateur.roles, e.roles)),
  })).filter((s) => s.entrees.length > 0);

  return (
    <>
      {/* Voile mobile */}
      {ouvert && (
        <div
          className="fixed inset-0 z-30 bg-ink/30 lg:hidden"
          onClick={onFermer}
          aria-hidden="true"
        />
      )}

      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line bg-surface',
          'transition-transform lg:static lg:translate-x-0',
          ouvert ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2.5 border-b border-line px-4">
          <LogoMarque
            className="h-8 max-w-[96px] shrink-0 object-contain"
            repli={
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary text-white">
                <span className="text-small font-bold">M</span>
              </span>
            }
          />
          <div className="min-w-0">
            <p className="truncate text-small font-semibold text-ink">Artivo</p>
            <p className="truncate text-caption text-ink-subtle">Plateforme</p>
          </div>
          <button
            type="button"
            onClick={onFermer}
            className="ml-auto rounded-lg p-1.5 text-ink-muted hover:bg-surface-alt lg:hidden"
            aria-label="Fermer la navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="defilement-fin flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {sections.map((section, i) => (
            <div key={section.titre ?? `section-${i}`}>
              {section.titre && (
                <p className="px-2 pb-1.5 text-overline uppercase text-ink-subtle">
                  {section.titre}
                </p>
              )}
              <ul className="space-y-0.5">
                {section.entrees.map((e) => (
                  <li key={e.vers}>
                    <NavLink
                      to={e.vers}
                      onClick={onFermer}
                      className={({ isActive }) =>
                        cx(
                          'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-small transition-colors',
                          isActive
                            ? 'bg-primary/10 font-medium text-primary'
                            : 'text-ink-muted hover:bg-surface-alt hover:text-ink',
                        )
                      }
                    >
                      <e.icone className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="truncate">{e.libelle}</span>
                      {e.compteur === 'avisFaibles' && <CompteurAvisFaibles />}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

function TopBar({ utilisateur, onMenu }: { utilisateur: AuthUser; onMenu: () => void }) {
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const naviguer = useNavigate();
  const deconnexion = useLogout();
  const emplacement = useLocation();

  // Referme le menu compte à chaque navigation.
  useEffect(() => setMenuOuvert(false), [emplacement.pathname]);

  const roles = utilisateur.roles.map((r) => ROLE_LABELS[r]).join(', ');

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-surface px-4">
      <button
        type="button"
        onClick={onMenu}
        className="rounded-lg p-2 text-ink-muted hover:bg-surface-alt lg:hidden"
        aria-label="Ouvrir la navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* La recherche du bandeau ouvre l'exploration sur le terme saisi :
          titres, tags et boutiques, dans le périmètre du compte. */}
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          const terme = recherche.trim();
          if (terme) naviguer(`/exploration?search=${encodeURIComponent(terme)}`);
        }}
        className="relative max-w-md flex-1"
      >
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
        <input
          type="search"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Rechercher un article, un tag, une boutique…"
          aria-label="Rechercher dans l'exploration"
          className="h-9 w-full rounded-lg border border-line bg-canvas pl-9 pr-3 text-small placeholder:text-ink-subtle focus:border-primary"
        />
      </form>

      {/* La recherche est plafonnée en largeur : ml-auto pousse les actions du compte à droite. */}
      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          className="relative rounded-lg p-2 text-ink-muted hover:bg-surface-alt"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOuvert((v) => !v)}
            aria-expanded={menuOuvert}
            aria-haspopup="menu"
            className="flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 hover:bg-surface-alt"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-accent/15 text-caption font-semibold text-accent">
              {utilisateur.prenom.charAt(0)}
              {utilisateur.nom.charAt(0)}
            </span>
            <span className="hidden text-left sm:block">
              <span className="block text-caption font-medium leading-tight text-ink">
                {utilisateur.prenom} {utilisateur.nom}
              </span>
              <span className="block text-caption leading-tight text-ink-subtle">{roles}</span>
            </span>
            <ChevronDown className="h-4 w-4 text-ink-subtle" />
          </button>

          {menuOuvert && (
            <div
              role="menu"
              className="absolute right-0 mt-1 w-56 animate-slide-up rounded-lg border border-line bg-surface p-1 shadow-modal"
            >
              <div className="border-b border-line px-3 py-2">
                <p className="truncate text-small font-medium text-ink">{utilisateur.email}</p>
                {utilisateur.familles.length > 0 && (
                  <p className="mt-0.5 text-caption text-ink-subtle">
                    Périmètre : {utilisateur.familles.length} famille
                    {utilisateur.familles.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <NavLink
                to="/changer-mot-de-passe"
                role="menuitem"
                className="block rounded px-3 py-2 text-small text-ink-muted hover:bg-surface-alt hover:text-ink"
              >
                Changer mon mot de passe
              </NavLink>
              <button
                type="button"
                role="menuitem"
                onClick={() => deconnexion.mutate()}
                className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-small text-ink-muted hover:bg-surface-alt hover:text-ink"
              >
                <LogOut className="h-4 w-4" />
                Se déconnecter
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/** Coquille commune à tous les espaces : même barre latérale, même en-tête. */
export function AppShell({ children }: { children: ReactNode }) {
  const [navOuverte, setNavOuverte] = useState(false);
  const { data: utilisateur } = useCurrentUser();
  const { data: devises } = useDevises();
  // Avant le rendu des pages : leurs prix se forment dans la devise d'affichage.
  definirDevises(devises);

  if (!utilisateur) return null;

  return (
    <div className="flex min-h-screen">
      <Sidebar
        utilisateur={utilisateur}
        ouvert={navOuverte}
        onFermer={() => setNavOuverte(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar utilisateur={utilisateur} onMenu={() => setNavOuverte(true)} />
        {/* Pleine largeur : pas de conteneur centré qui laisserait des marges mortes. */}
        <main className="flex-1 px-4 py-6 lg:px-6">{children}</main>
      </div>
    </div>
  );
}
